// Inverse of buildQueries.ts. The FE reconstructs a widget's backend QuerySpec
// array from its config's *_builder_misc (UI form state) on every serialize, and
// ignores any stored top-level `queries`. Sandbox AI authors the backend `queries`
// directly and leaves *_builder_misc empty, so an applied AI widget would serialize
// to empty queries and fail schema validation on autosave. This module hydrates the
// builder-misc from the authored queries so AI widgets round-trip through
// serializeDashboardSpec (and are editable in the configurators).
//
// The query-bearing widget types are handled (chart, table, pie_chart, stat_tile,
// player_notes, table_2d, gauge, radar, body_map, scatterplot); content and player_card
// carry no queries and pass through unchanged. Keep this in lock-step with buildQueries.ts —
// each case below reproduces exactly the fields its buildQueriesFor* counterpart reads.

import { DEFAULT_LATEST_N } from "../AddWidgetModal/constants";
import type { ComponentSpec, ControllerBindingFields } from "./types";

interface BackendDate {
  type?: "relative" | "absolute" | "latest_n";
  anchor?: string;
  offset_days?: number;
  value?: string;
  /** latest_n only: how many dates with data the window holds. */
  n?: number;
}

interface BackendQuery {
  id: string;
  metric_identifier?: { key: string; type: string } | null;
  count?: {
    source: "flags" | "statuses" | "injuries" | "treatments";
    include?: string[];
    match?: "all" | "any";
    reading?: "incidence" | "prevalence";
  } | null;
  filter?: {
    date_filter?: { start_date?: BackendDate; end_date?: BackendDate; day_filter?: string[] };
    subject_filter?: string[];
    extra_filters?: Record<string, unknown> | null;
    date_controller_id?: string;
    subject_controller_id?: string;
    subject_controller_mode?: "positions";
    single_date_controller_id?: string;
    single_player_controller_id?: string;
  };
  groupby?: Array<{ field: string; grain?: string }>;
  metadata?: Record<string, unknown> | null;
}

/** The builder-misc shape for a query's `count`, or undefined for a measured metric.
 *  Mirrors `countFields` in buildQueries.ts — keep the two in lock-step. */
function invCount(q: BackendQuery | undefined) {
  const c = q?.count;
  if (!c) return undefined;
  return {
    source: c.source,
    // The backend canonicalises with sorted(set(...)). An AI-authored spec may arrive
    // unsorted or with duplicates, and the summary label names include[0] — so without
    // matching that here the label could name a different item than the backend's own
    // canonical order.
    include: Array.from(new Set(c.include ?? [])).sort(),
    match: c.match ?? ("all" as const),
    ...(c.source === "injuries" ? { reading: c.reading ?? ("incidence" as const) } : {}),
  };
}

// "gps-2-sum" → { metricId: "gps-2", agg: "sum" }; "gps-c5-avg" → { "gps-c5", "avg" }.
function splitMetricKey(key: string): { metricId: string; agg: string } {
  const idx = key.lastIndexOf("-");
  if (idx <= 0) return { metricId: key, agg: "avg" };
  return { metricId: key.slice(0, idx), agg: key.slice(idx + 1) };
}

// Reverse of mapGroupBy(): a groupby array → the builder's single groupBy string.
function invGroupBy(groupby?: Array<{ field: string; grain?: string }>): string | undefined {
  if (!groupby || groupby.length === 0) return undefined;
  const g = groupby[0];
  if (g.field === "time") return g.grain ?? "day";
  if (g.field === "player") return "player";
  if (g.field === "pos") return "position";
  if (g.field === "drill") return "drill";
  return undefined;
}

// Reproduce a DateSpec via buildDateFilter's advanced-mode fields (explicit offsets/
// absolutes), so any authored date range round-trips exactly without preset inference.
function dateFieldsFromFilter(dateFilter?: {
  start_date?: BackendDate;
  end_date?: BackendDate;
  day_filter?: string[];
}): Record<string, unknown> {
  const start = dateFilter?.start_date ?? {};
  const end = dateFilter?.end_date ?? {};
  const startType =
    start.type === "absolute" || start.type === "latest_n" ? start.type : "relative";
  const fields: Record<string, unknown> = {
    showAdvancedDate: true,
    startDateType: startType,
    endDateType: end.type === "absolute" ? "absolute" : "relative",
    dayFilter: dateFilter?.day_filter ?? [],
  };
  // A latest_n window carries a COUNT, not a date. Reading it as relative was how the
  // window silently became a 0-day range the first time a coach opened the widget.
  if (start.type === "latest_n") fields.startDateLatestN = start.n ?? DEFAULT_LATEST_N;
  else if (start.type === "absolute") fields.startDateAbsolute = start.value ?? "";
  else fields.startDateOffset = start.offset_days ?? 0;
  if (end.type === "absolute") fields.endDateAbsolute = end.value ?? "";
  else fields.endDateOffset = end.offset_days ?? 0;
  return fields;
}

function subjectsFromFilter(subjectFilter?: string[]): {
  selectedSubjects: string[];
  selectedDrills: string[];
} {
  const list = subjectFilter ?? [];
  // The engine treats ANY full-team entry as whole-team; mirroring that here keeps
  // the rebuilt query's scope identical instead of narrowing to the residual entries.
  if (list.length === 0 || list.includes("full-team")) {
    return { selectedSubjects: [], selectedDrills: [] };
  }
  return {
    selectedDrills: list.filter((s) => s.startsWith("drill-")),
    selectedSubjects: list.filter((s) => s !== "full-team" && !s.startsWith("drill-")),
  };
}

function excludeInjuredFrom(filter?: { extra_filters?: Record<string, unknown> | null }): boolean {
  const v = filter?.extra_filters?.exclude_injured_players;
  return typeof v === "boolean" ? v : true;
}

type QueryFilter = NonNullable<BackendQuery["filter"]>;

// Slicer bindings ride on the query filter (controllerRefs in buildQueries.ts), so they
// must ride back. A chart series that inherits Plot 1's binding has no id in its own
// misc; without this the binding survives on Plot 1 only.
function bindingFieldsFromFilter(filter?: QueryFilter): Partial<ControllerBindingFields> {
  return {
    dateControllerId: filter?.date_controller_id,
    subjectControllerId: filter?.subject_controller_id,
    subjectControllerMode: filter?.subject_controller_mode,
  };
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// "Same as Plot 1" holds only while this query still says what Plot 1's query says —
// an authored query may give the series a filter of its own.
function inheritsDate(q: BackendQuery, first: BackendQuery | undefined): boolean {
  return !!first
    && sameJson(q.filter?.date_filter, first.filter?.date_filter)
    && q.filter?.date_controller_id === first.filter?.date_controller_id;
}

function inheritsSubjects(q: BackendQuery, first: BackendQuery | undefined): boolean {
  return !!first
    && sameJson(q.filter?.subject_filter, first.filter?.subject_filter)
    && q.filter?.subject_controller_id === first.filter?.subject_controller_id
    && q.filter?.subject_controller_mode === first.filter?.subject_controller_mode
    && excludeInjuredFrom(q.filter) === excludeInjuredFrom(first.filter);
}

/**
 * Populate a widget config's *_builder_misc from its backend queries so it survives
 * the serializeDashboardSpec round-trip. Returns the config unchanged for content
 * widgets and when no queries are present. Operates on a deep clone; the input is
 * not mutated.
 */
export function hydrateWidgetConfigFromQueries(
  config: ComponentSpec,
  queries: ReadonlyArray<Record<string, unknown>> | undefined,
  type: string,
): ComponentSpec {
  if (!config || !queries || queries.length === 0) return config;

  const qs = queries as unknown as BackendQuery[];
  const byId = new Map(qs.map((q) => [q.id, q]));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = JSON.parse(JSON.stringify(config));

  switch (type) {
    case "chart": {
      c.chart_builder_misc = {
        ...(c.chart_builder_misc ?? {}),
        groupBy: invGroupBy(qs[0].groupby) ?? c.chart_builder_misc?.groupBy ?? "day",
      };
      const first = byId.get((c.series ?? [])[0]?.queryId) ?? qs[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      c.series = (c.series ?? []).map((s: any, idx: number) => {
        const q = byId.get(s.queryId);
        if (!q || !q.metric_identifier) return s;
        const misc = s.series_builder_misc ?? {};
        // A companion reads its dates and subjects from its parent (seriesMisc.ts),
        // so only its metric is restored.
        if (misc.isAcwrCompanion === true) {
          return {
            ...s,
            series_builder_misc: {
              ...misc,
              selectedMetricId: splitMetricKey(q.metric_identifier.key).metricId,
            },
          };
        }
        const subs = subjectsFromFilter(q.filter?.subject_filter);
        return {
          ...s,
          series_builder_misc: {
            ...misc,
            countSpec: invCount(q),
            selectedMetricId: splitMetricKey(q.metric_identifier.key).metricId,
            inheritDateRange: idx > 0 && misc.inheritDateRange === true && inheritsDate(q, first),
            inheritPlayers: idx > 0 && misc.inheritPlayers === true && inheritsSubjects(q, first),
            ...dateFieldsFromFilter(q.filter?.date_filter),
            selectedSubjects: subs.selectedSubjects,
            selectedDrills: subs.selectedDrills,
            excludeInjuredPlayers: excludeInjuredFrom(q.filter),
            ...bindingFieldsFromFilter(q.filter),
          },
        };
      });
      return c as ComponentSpec;
    }

    case "table": {
      const first = qs[0];
      const columnMetrics: Record<string, string> = {};
      const countSpecs: Record<string, NonNullable<ReturnType<typeof invCount>>> = {};
      for (const q of qs) {
        if (q.metric_identifier) columnMetrics[q.id] = splitMetricKey(q.metric_identifier.key).metricId;
        const countSpec = invCount(q);
        if (countSpec) countSpecs[q.id] = countSpec;
      }
      const subs = subjectsFromFilter(first.filter?.subject_filter);
      c.table_builder_misc = {
        ...(c.table_builder_misc ?? {}),
        countSpecs,
        groupBy: invGroupBy(first.groupby) ?? c.table_builder_misc?.groupBy ?? "day",
        columnMetrics,
        ...dateFieldsFromFilter(first.filter?.date_filter),
        selectedSubjects: subs.selectedSubjects,
        selectedDrills: subs.selectedDrills,
        excludeInjuredPlayers: excludeInjuredFrom(first.filter),
        ...bindingFieldsFromFilter(first.filter),
      };
      return c as ComponentSpec;
    }

    case "pie_chart": {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      c.series = (c.series ?? []).map((s: any) => {
        const q = byId.get(s.queryId);
        if (!q || !q.metric_identifier) return s;
        const subs = subjectsFromFilter(q.filter?.subject_filter);
        return {
          ...s,
          series_builder_misc: {
            ...(s.series_builder_misc ?? {}),
            countSpec: invCount(q),
            selectedMetricId: splitMetricKey(q.metric_identifier.key).metricId,
            // The edit modal reads the slice label only from labelOverride; carry an
            // authored displayNameOverride into it so a modal save doesn't drop it.
            labelOverride: s.series_builder_misc?.labelOverride || s.displayNameOverride,
            ...dateFieldsFromFilter(q.filter?.date_filter),
            selectedSubjects: subs.selectedSubjects,
            selectedDrills: subs.selectedDrills,
            excludeInjuredPlayers: excludeInjuredFrom(q.filter),
            ...bindingFieldsFromFilter(q.filter),
          },
        };
      });
      return c as ComponentSpec;
    }

    case "stat_tile": {
      const q = byId.get(c.queryId) ?? qs[0];
      if (q && q.metric_identifier) {
        const { metricId, agg } = splitMetricKey(q.metric_identifier.key);
        const subs = subjectsFromFilter(q.filter?.subject_filter);
        const meta = (q.metadata ?? {}) as Record<string, unknown>;
        const comparison =
          (meta.comparison_key as string) ||
          c.stat_tile_builder_misc?.comparisonType ||
          "latest_vs_prev";
        // Stat tiles carry both flags in query METADATA (buildQueriesForStat),
        // not filter.extra_filters — read them from there so an authored
        // exclude_injured_players / show_team_pos_data survives apply → autosave
        // (and the renderer, which reads showTeamPosData from builder_misc).
        const excludeInjured =
          typeof meta.exclude_injured_players === "boolean"
            ? meta.exclude_injured_players
            : excludeInjuredFrom(q.filter);
        c.stat_tile_builder_misc = {
          ...(c.stat_tile_builder_misc ?? {}),
          countSpec: invCount(q),
          selectedMetricId: metricId,
          metricAgg: agg,
          comparisonType: comparison,
          ...dateFieldsFromFilter(q.filter?.date_filter),
          selectedSubjects: subs.selectedSubjects,
          selectedDrills: subs.selectedDrills,
          excludeInjuredPlayers: excludeInjured,
          showTeamPosData: meta.show_team_pos_data !== false,
          ...bindingFieldsFromFilter(q.filter),
        };
      }
      return c as ComponentSpec;
    }

    case "player_notes": {
      // Reverse buildQueriesForPlayerNotes: no metric, exclude_injured defaults FALSE,
      // subjects verbatim-minus-full-team (NO drill split — notes have no drill dim).
      const q = byId.get(c.queryId) ?? qs[0];
      if (q) {
        const extra = (q.filter?.extra_filters ?? {}) as Record<string, unknown>;
        const subjectFilter = q.filter?.subject_filter ?? [];
        const selectedSubjects = subjectFilter.includes("full-team")
          ? []
          : subjectFilter.filter((s) => s !== "full-team");
        const misc: Record<string, unknown> = {
          ...(c.player_notes_builder_misc ?? {}),
          noteIds: Array.isArray(extra.note_ids) ? (extra.note_ids as unknown[]).map(String) : [],
          excludeInjuredPlayers:
            typeof extra.exclude_injured_players === "boolean" ? extra.exclude_injured_players : false,
          ...dateFieldsFromFilter(q.filter?.date_filter),
          selectedSubjects,
          ...bindingFieldsFromFilter(q.filter),
        };
        if (typeof extra.search_pattern === "string" && extra.search_pattern) {
          misc.searchPattern = extra.search_pattern;
        }
        c.player_notes_builder_misc = misc;
      }
      return c as ComponentSpec;
    }

    case "table_2d": {
      // buildQueriesForTable2D rebuilds queries from table_2d_builder_misc.metrics
      // (index-paired with queryIds) + the shared date/subject fields. groupby comes
      // from rowDimension/colDimension, so only metrics + filter fields need hydrating.
      const queryIds: string[] = (c.queryIds ?? []) as string[];
      const metrics = queryIds.map((qid) => {
        const q = byId.get(qid);
        if (!q || !q.metric_identifier) return { queryId: qid, metricId: null, metricAgg: "avg" };
        const { metricId, agg } = splitMetricKey(q.metric_identifier.key);
        return { queryId: qid, metricId, metricAgg: agg };
      });
      const first = byId.get(queryIds[0]) ?? qs[0];
      const subs = subjectsFromFilter(first?.filter?.subject_filter);
      c.table_2d_builder_misc = {
        ...(c.table_2d_builder_misc ?? {}),
        countSpecs: Object.fromEntries(
          qs.filter((q) => q.count).map((q) => [q.id, invCount(q)!]),
        ),
        metrics,
        ...dateFieldsFromFilter(first?.filter?.date_filter),
        selectedSubjects: subs.selectedSubjects,
        selectedDrills: subs.selectedDrills,
        excludeInjuredPlayers: excludeInjuredFrom(first?.filter),
        ...bindingFieldsFromFilter(first?.filter),
      };
      return c as ComponentSpec;
    }

    case "gauge": {
      // Populate gauge_builder_misc from the needle (+ scale) query, and RE-ADD the
      // config-level metricIdentifierType the backend GaugeSpec strips on persist.
      const needle = byId.get(c.queryId) ?? qs[0];
      if (!needle || !needle.metric_identifier) return config;
      const { metricId, agg } = splitMetricKey(needle.metric_identifier.key);
      const subs = subjectsFromFilter(needle.filter?.subject_filter);
      const miType = needle.metric_identifier.type ?? "metric";
      c.metricIdentifierType = miType;
      let scaleSubjectFilter: string[] = [];
      if (c.scaleQueryId) {
        const scaleQ = byId.get(c.scaleQueryId);
        scaleSubjectFilter = (scaleQ?.filter?.subject_filter ?? []) as string[];
      }
      c.gauge_builder_misc = {
        ...(c.gauge_builder_misc ?? {}),
        countSpec: invCount(needle),
        selectedMetricId: metricId,
        metricAgg: agg,
        metricIdentifierType: miType,
        ...dateFieldsFromFilter(needle.filter?.date_filter),
        selectedSubjects: subs.selectedSubjects,
        selectedDrills: subs.selectedDrills,
        excludeInjuredPlayers: excludeInjuredFrom(needle.filter),
        scaleSubjectFilter,
        ...bindingFieldsFromFilter(needle.filter),
      };
      return c as ComponentSpec;
    }

    case "radar": {
      // buildQueriesForRadar's loop is identical for metric- and entity-vertex
      // (series[s].queryIds[i] is always metric i), so the metric list comes from the
      // FIRST series' queryIds. Do NOT rebuild series — the AI authored those.
      const firstSeries = (c.series ?? [])[0];
      const vertexQids: string[] = firstSeries?.queryIds ?? [];
      const metricNames: string[] = c.metric_names ?? [];
      const isCustom = c.normalization === "custom";
      const isMetricVertex = (c.vertexDimension ?? "metric") === "metric";
      const priorMetrics = c.radar_builder_misc?.metrics ?? [];
      const metrics = vertexQids
        .map((qid: string, i: number) => {
          const q = byId.get(qid);
          if (!q || !q.metric_identifier) return priorMetrics[i] ?? null;
          const { metricId, agg } = splitMetricKey(q.metric_identifier.key);
          const meta = (q.metadata ?? {}) as Record<string, unknown>;
          const entry: Record<string, unknown> = {
            ...(priorMetrics[i] ?? {}),
            metricId,
            metricAgg: agg,
            metricIdentifierType: q.metric_identifier.type ?? "metric",
            displayName: priorMetrics[i]?.displayName ?? metricNames[i] ?? "",
            displayNameOverride: priorMetrics[i]?.displayNameOverride ?? metricNames[i],
          };
          if (isCustom && isMetricVertex) {
            if (typeof meta.custom_min === "number") entry.customMin = meta.custom_min;
            if (typeof meta.custom_max === "number") entry.customMax = meta.custom_max;
          }
          return entry;
        })
        .filter(Boolean);
      const refQ = byId.get(vertexQids[0]) ?? qs[0];
      c.radar_builder_misc = {
        ...(c.radar_builder_misc ?? {}),
        ...dateFieldsFromFilter(refQ?.filter?.date_filter),
        dateControllerId: refQ?.filter?.date_controller_id,
        metrics,
        vertexDimension: c.vertexDimension ?? "metric",
      };
      return c as ComponentSpec;
    }

    case "scatterplot": {
      // Reverse buildQueriesForScatterplot: both queries share ONE filter and ONE
      // groupby, so the widget-level misc hydrates from the Y query (always present;
      // date mode has no X query) and each axis misc takes its own metric key.
      const yQ = byId.get(c.yAxis?.queryId) ?? qs[0];
      const xQ = c.xAxis ? byId.get(c.xAxis.queryId) : undefined;
      for (const [axis, q] of [[c.xAxis, xQ], [c.yAxis, yQ]] as const) {
        if (!axis || !q?.metric_identifier) continue;
        const { metricId, agg } = splitMetricKey(q.metric_identifier.key);
        axis.metricAgg = agg;
        axis.axis_builder_misc = {
          ...(axis.axis_builder_misc ?? {}),
          selectedMetricId: metricId,
        };
      }
      const entityField = (yQ?.groupby ?? []).find((g) => g.field !== "time")?.field;
      const subs = subjectsFromFilter(yQ?.filter?.subject_filter);
      c.scatterplot_builder_misc = {
        ...(c.scatterplot_builder_misc ?? {}),
        ...(entityField && { groupBy: entityField === "pos" ? "position" : entityField }),
        ...dateFieldsFromFilter(yQ?.filter?.date_filter),
        selectedSubjects: subs.selectedSubjects,
        selectedDrills: subs.selectedDrills,
        excludeInjuredPlayers: excludeInjuredFrom(yQ?.filter),
      };
      return c as ComponentSpec;
    }

    case "body_map": {
      // Reverse buildQueriesForBodyMap: read bodymap_use_latest FIRST (it wins over the
      // placeholder date), extract the single player-N subject (no subjectsFromFilter).
      const q = byId.get(c.queryId) ?? qs[0];
      if (q) {
        const useLatest = q.filter?.extra_filters?.bodymap_use_latest === true;
        const start = q.filter?.date_filter?.start_date ?? {};
        const playerSubject =
          (q.filter?.subject_filter ?? []).find((s) => s.startsWith("player-")) ?? "";
        let dateMode: "relative" | "absolute" | "latest";
        let dateOffset = 0;
        let fixedDate = "";
        if (useLatest) {
          dateMode = "latest";
        } else if (start.type === "absolute") {
          dateMode = "absolute";
          fixedDate = start.value ?? "";
        } else {
          dateMode = "relative";
          dateOffset = start.offset_days ?? 0;
        }
        c.body_map_builder_misc = {
          ...(c.body_map_builder_misc ?? {}),
          playerSubject,
          dateMode,
          dateOffset,
          fixedDate,
          singleDateControllerId: q.filter?.single_date_controller_id,
          singlePlayerControllerId: q.filter?.single_player_controller_id,
        };
      }
      return c as ComponentSpec;
    }

    default:
      return config;
  }
}
